import {
  selectedHistory,
  statisticObservation,
  conversionObservation,
} from "./teamAnalysis";

// Same weighted sum as statistic-model/model.attackMoments. Never fill missing inputs.
export function modelMoments(state, id, field) {
  const terms = Object.entries(state?.weights || {})
    .filter(([, w]) => w !== 0)
    .map(([key, weight]) => ({
      key,
      weight,
      value: (() => {
        const values = state?.teams?.[id]?.statistics?.[key];
        const raw = values?.[field];
        if (key !== "45") return raw;
        const venue = field.startsWith("home") ? "home" : "away";
        const total = values?.[venue + "_for"] + values?.[venue + "_against"];
        // Same possession-pair normalization as matchup against league reference.
        return Number.isFinite(raw) && total > 0 ? raw / total : null;
      })(),
    }));
  return {
    terms,
    value:
      terms.length && terms.every((t) => Number.isFinite(t.value))
        ? terms.reduce((n, t) => n + t.value * t.weight, 0)
        : null,
  };
}
export function statisticAuditCell(
  data,
  proposed,
  id,
  key,
  field,
  source,
  totals = false
) {
  const historical = source === "observed" || source === "expected";
  if (!historical) {
    const state = source === "saved" ? data.state : proposed;
    const moments = modelMoments(state, id, field);
    const rate = state?.teams?.[id]?.conversion?.[field];
    const value =
      key === "moments"
        ? moments.value
        : key === "goals"
        ? Number.isFinite(moments.value) && Number.isFinite(rate)
          ? moments.value * rate
          : null
        : key === "conversion"
        ? rate
        : state?.teams?.[id]?.statistics?.[key]?.[field];
    return {
      value,
      terms: moments.terms,
      rate,
      moments: moments.value,
      source,
      model: true,
    };
  }
  const games = selectedHistory(data.observations || [], id);
  const expected = source === "expected";
  const venue = field.startsWith("home") ? "home" : "away";
  const fixtures = games
    .filter((g) => Number(g[venue + "_team_id"]) === Number(id))
    .filter((g) => {
      const own = Number(g.home_team_id) === Number(id) ? "home" : "away";
      const side = field.endsWith("for")
        ? own
        : own === "home"
        ? "away"
        : "home";
      if (["moments", "conversion", "goals"].includes(key)) {
        const sample = g.conversion?.[expected ? "expected" : "actual"]?.[side];
        return (
          Number.isFinite(sample?.goals) &&
          sample.goals >= 0 &&
          Number.isFinite(sample?.moments) &&
          sample.moments > 0
        );
      }
      return Number.isFinite(
        (expected ? g.statistics_model?.expected : g.statistics)?.[key]?.[side]
      );
    })
    .map((g) => ({
      id: g.id,
      home: g.home_name,
      away: g.away_name,
      goals: g.goals,
    }));
  if (["moments", "conversion", "goals"].includes(key)) {
    const c = conversionObservation(games, id, field, expected);
    return {
      value:
        key === "conversion"
          ? c.mean
          : key === "moments"
          ? c.count
            ? c.moments / (totals ? 1 : c.count)
            : null
          : c.count
          ? c.goals / (totals ? 1 : c.count)
          : null,
      fixtures,
      count: c.count,
      selected: c.selected,
      rate: c.mean,
      moments: c.moments,
      goals: c.goals,
      source,
    };
  }
  const s = statisticObservation(games, id, key, field, expected);
  // Possession is a rate, including when the other columns show totals.
  const value =
    s.mean === null ? null : s.mean * (totals && key !== "45" ? s.count : 1);
  return {
    ...s,
    fixtures,
    value: key === "45" && value > 1 ? value / 100 : value,
    source,
  };
}
export function auditSort(a, b, direction) {
  const x = a.value,
    y = b.value;
  if (!Number.isFinite(x)) return Number.isFinite(y) ? 1 : 0;
  if (!Number.isFinite(y)) return -1;
  return (x - y) * (direction === "asc" ? 1 : -1);
}
